import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactNode } from 'react';
import styled from 'styled-components';

import { Position, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineL } from '@sorare/core/src/atoms/typography';
import { EmptySlot } from '@sorare/core/src/components/collections/EmptySlot';
import { useLowestSalePerPlayer } from '@sorare/core/src/components/collections/useLowestSalePerPlayer';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { ANY_SPORT_PLAYERS_SLUG_CARDS } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { isType } from '@sorare/core/src/gql';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { FulfilledSlot } from 'components/collections/FulfilledSlot';

import {
  CollectionSlots_cardCollection,
  CollectionSlots_cardCollectionSlot,
  CollectionSlots_userCardCollectionSlot,
} from './__generated__/index.graphql';

const Root = styled(Vertical)`
  padding: var(--quadruple-unit) 0;
`;

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  column-gap: var(--double-unit);
  row-gap: var(--triple-unit);
  @media ${tabletAndAbove} {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
`;

const getPlayerSlugsFromEmptySlots = (
  slots:
    | CollectionSlots_userCardCollectionSlot[]
    | CollectionSlots_cardCollectionSlot[]
) => {
  const result: string[] = [];
  slots.forEach(slot => {
    if (isType(slot, 'CardCollectionSlot')) {
      result.push(slot.player.slug);
    } else if (slot.cardCollectionCardsCount === 0) {
      result.push(slot.slot.player.slug);
    }
  });
  return result;
};

type Props = {
  slots:
    | CollectionSlots_userCardCollectionSlot[]
    | CollectionSlots_cardCollectionSlot[];
  cardCollection: CollectionSlots_cardCollection;
  readOnly: boolean;
  groupBy?: {
    id: string;
    label: ReactNode;
    positions: Position[];
  }[];
};

export const CollectionSlots = ({
  cardCollection,
  slots,
  readOnly,
  groupBy = [{ id: 'default', label: null, positions: [] }],
}: Props) => {
  const { generateSportContextPath, sport = Sport.FOOTBALL } =
    useSportContext();
  const emptySlotsSlugs = getPlayerSlugsFromEmptySlots(slots);
  const { season, rarity, team } = cardCollection;
  const { saleByPlayer } = useLowestSalePerPlayer({
    slugs: emptySlotsSlugs,
    sport,
    season: season?.startYear,
    rarity,
    teamSlug: team?.slug,
    skip: readOnly,
  });

  const groupBySlots = groupBy.reduce<
    {
      id: string;
      label: ReactNode;
      slots: (
        | CollectionSlots_userCardCollectionSlot
        | CollectionSlots_cardCollectionSlot
      )[];
      completedCount: number;
    }[]
  >((acc, curr) => {
    const { positions, label, id } = curr;
    const slotsByGroup = slots.filter(
      slot =>
        positions.length === 0 ||
        (isType(slot, 'UserCardCollectionSlot') &&
          positions.includes(slot?.slot.player.anyPositions?.[0])) ||
        (isType(slot, 'CardCollectionSlot') &&
          positions.includes(slot.player.anyPositions?.[0]))
    );
    const completedCount = slotsByGroup.reduce((sum, slot) => {
      if (isType(slot, 'UserCardCollectionSlot')) {
        return sum + (slot.cardCollectionCardsCount > 0 ? 1 : 0);
      }
      return sum;
    }, 0);
    return [
      ...(acc?.length ? acc : []),
      {
        id,
        label,
        slots: slotsByGroup,
        completedCount,
      },
    ];
  }, []);

  return (
    <Root gap={5}>
      {groupBySlots?.map(group => (
        <Vertical key={group.id} gap={3}>
          {group.label && (
            <HeadlineL>
              {group.label}
              {` ${group.completedCount}`}
              <HeadlineL as="span" color="var(--c-neutral-600">
                /{`${group.slots.length}`}
              </HeadlineL>
            </HeadlineL>
          )}
          <Wrapper>
            {group.slots.map(userSlot => {
              const slot = isType(userSlot, 'UserCardCollectionSlot')
                ? userSlot.slot
                : userSlot;

              if (isType(userSlot, 'UserCardCollectionSlot')) {
                const { slug, cardCollectionCardsCount } = userSlot;
                const isFilled = cardCollectionCardsCount > 0;
                if (isFilled) {
                  return (
                    <FulfilledSlot
                      key={slug}
                      bannerPictureUrl={cardCollection.bannerPictureUrl || ''}
                      userSlot={userSlot}
                      cardCollection={cardCollection}
                    />
                  );
                }
              }

              const { player, cardPictureUrl } = slot;

              const amount = saleByPlayer[player.slug]?.amount;
              const transferMarketFilters = {
                [SEARCH_PARAMS.TEAM]: `${cardCollection.team?.name}|${cardCollection.team?.slug}`,
                ...(cardCollection.rarity && {
                  [SEARCH_PARAMS.RARITY]: cardCollection.rarity,
                }),
                ...(cardCollection.season && {
                  [SEARCH_PARAMS.SEASON]: `${cardCollection.season.startYear}`,
                }),
                ...(amount && { [SEARCH_PARAMS.ON_SALE]: 'true' }),
              };
              const transferMarketFiltersStr = new URLSearchParams(
                transferMarketFilters
              ).toString();
              return (
                <EmptySlot
                  key={slot.id}
                  to={`${generateSportContextPath(
                    ANY_SPORT_PLAYERS_SLUG_CARDS,
                    {
                      params: {
                        slug: player.slug,
                      },
                    }
                  )}?${transferMarketFiltersStr}`}
                  pictureUrl={
                    saleByPlayer[player.slug]?.pictureUrl ?? cardPictureUrl
                  }
                  amount={amount}
                  displayName={player.displayName}
                  readOnly={readOnly}
                />
              );
            })}
          </Wrapper>
        </Vertical>
      ))}
    </Root>
  );
};

CollectionSlots.fragments = {
  userCardCollectionSlot: gql`
    fragment CollectionSlots_userCardCollectionSlot on UserCardCollectionSlot {
      slug
      cardCollectionCardsCount
      slot {
        id
        cardPictureUrl
        player {
          slug
          displayName
          anyPositions
        }
      }
      ...FulfilledSlot_userCardCollectionSlot
    }
    ${FulfilledSlot.fragments.userCardCollectionSlot}
  ` as TypedDocumentNode<CollectionSlots_userCardCollectionSlot>,
  cardCollectionSlot: gql`
    fragment CollectionSlots_cardCollectionSlot on CardCollectionSlot {
      id
      cardPictureUrl
      player {
        slug
        displayName
        anyPositions
      }
    }
  ` as TypedDocumentNode<CollectionSlots_cardCollectionSlot>,
  cardCollection: gql`
    fragment CollectionSlots_cardCollection on CardCollection {
      slug
      rarity
      bannerPictureUrl
      season {
        startYear
      }
      team {
        ... on TeamInterface {
          slug
        }
      }
      ...FulfilledSlot_cardCollection
    }
    ${FulfilledSlot.fragments.cardCollection}
  ` as TypedDocumentNode<CollectionSlots_cardCollection>,
};
